import { useState, useCallback, useEffect } from 'react';
import { vocabularyLevelChecker } from '@/lib/vocabularyLevel';
import { lookupVocabulary } from '@/lib/vocabulary-lookup-client';
import { UserSettings } from '@/contexts/user-settings-context';
import { useSavedWordsContext } from '@/contexts/saved-words-context';

interface LookupMetadata {
  source?: 'lookup';
  definition?: string | null;
  tag?: string | null;
  pos?: string | null;
  bnc?: number | null;
  frq?: number | null;
}

interface WordExplanation {
  translation: string;
  example?: string;
  explanation?: string;
  usphone?: string;
  lookupMetadata?: LookupMetadata;
  // 🎯 用于 vocabulary hover card 显示
  contextDescription?: string;
  fullTerm?: string;
  source?: 'vocabulary' | 'youdao' | 'dictionary';
}

const FALLBACK_EXAMPLES = new Set([
  'Could not load example. Please try again later.',
  'Error fetching example',
]);

interface SaveCardOptions {
  youdaoTranslation?: {
    translation?: string;
    phonetic?: string;
    usphone?: string;
    explains?: string[];
  };
  episodeId?: string;
  episodeTitle?: string;
  contextText?: string;
  episodeThumbnail?: string;
  // 新增：保存时的词汇分类
  status?: 'NEW' | 'FUZZY' | 'MASTERED';
}

interface UseWordExplanationReturn {
  explanations: Record<string, WordExplanation>;
  loading: Record<string, boolean>;
  savedWords: Record<string, boolean>;
  savingWord: Record<string, boolean>;
  getExplanation: (term: string) => Promise<void>;
  saveCard: (word: string, options?: SaveCardOptions) => Promise<void>;
  isCardSaved: (word: string) => boolean;
}

export function useWordExplanation(
  userSettings?: UserSettings,
  sourceText?: string,
  userId?: string
): UseWordExplanationReturn {
  const [explanations, setExplanations] = useState<Record<string, WordExplanation>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [savingWord, setSavingWord] = useState<Record<string, boolean>>({});

  // Use shared saved words context
  const { savedWords, isCardSaved: contextIsCardSaved, markWordAsSaved } = useSavedWordsContext();

  // 内存优化：定期清理过大的状态对象
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // 清理explanations - 只保留最近300个
      setExplanations(prev => {
        const entries = Object.entries(prev);
        if (entries.length > 300) {
          const recent = entries.slice(-200); // 保留最近200个
          return Object.fromEntries(recent);
        }
        return prev;
      });

      // 清理loading状态 - 只保留正在加载的
      setLoading(prev => {
        const activeLoading = Object.entries(prev).filter(([, isLoading]) => isLoading);
        if (Object.keys(prev).length > activeLoading.length) {
          return Object.fromEntries(activeLoading);
        }
        return prev;
      });
    }, 60000); // 每分钟清理一次

    return () => clearInterval(cleanupInterval);
  }, []);

  const getExplanation = useCallback(async (term: string) => {
    const cleanedTerm = (term ?? '').trim();
    if (!cleanedTerm) {
      return;
    }

    const normalizedTerm = cleanedTerm.toLowerCase();

    let shouldSkip = false;
    setExplanations((prev) => {
      const existing = prev[cleanedTerm];
      if (existing) {
        const translationText = typeof existing.translation === 'string' ? existing.translation.trim().toLowerCase() : '';
        const exampleText = existing.example?.trim() || '';
        const isFallbackTranslation = !translationText || translationText === normalizedTerm;
        const isFallbackExample = exampleText && FALLBACK_EXAMPLES.has(exampleText);

        if (isFallbackTranslation || isFallbackExample) {
          const next = { ...prev };
          delete next[cleanedTerm];
          return next;
        }

        shouldSkip = true;
      }
      return prev;
    });

    if (shouldSkip) return;

    setLoading((prev) => ({ ...prev, [cleanedTerm]: true }));

    try {
      // Check if user's language setting is NOT Chinese
      const userLanguage = userSettings?.languageSetting?.toLowerCase() || 'english';
      const isNonChinese = userLanguage !== 'zh' && userLanguage !== 'chinese' && userLanguage !== 'zh-cn' && userLanguage !== 'zh-hans';

      if (!isNonChinese && await vocabularyLevelChecker.hasTranslationAsync(cleanedTerm)) {
        const translation = vocabularyLevelChecker.getTranslation(cleanedTerm);
        const usphone = vocabularyLevelChecker.getUSPronunciation(cleanedTerm);

        setExplanations((prev) => ({
          ...prev,
          [cleanedTerm]: {
            translation: Array.isArray(translation) ? translation.join('; ') : translation || `${cleanedTerm} (local dictionary)`,
            example: '',
            usphone: usphone || undefined,
          },
        }));
        setLoading((prev) => ({ ...prev, [cleanedTerm]: false }));
        return;
      }

      // Only use Turso lookup if language is Chinese (or default/English which might map to zh context in legacy)
      // Actually, Turso dictionary seems to be Chinese-centric or bilingual (EN-ZH).
      // If non-Chinese, skip Turso lookup to force AI explanation.
      let lookupResult = null;
      if (!isNonChinese) {
        lookupResult = await lookupVocabulary(cleanedTerm);
      }

      if (lookupResult) {
        const primaryTranslation =
          (typeof lookupResult.translation === 'string' && lookupResult.translation.trim())
            ? lookupResult.translation.trim()
            : undefined;

        const fallbackDefinition =
          (typeof lookupResult.definition === 'string' && lookupResult.definition.trim())
            ? lookupResult.definition.trim()
            : undefined;

        setExplanations((prev) => ({
          ...prev,
          [cleanedTerm]: {
            translation: primaryTranslation || fallbackDefinition || `${cleanedTerm} (turso dictionary)`,
            explanation: fallbackDefinition,
            example: '',
            usphone: (typeof lookupResult.phonetic === 'string' && lookupResult.phonetic.trim())
              ? lookupResult.phonetic.trim()
              : undefined,
            lookupMetadata: {
              source: 'lookup',
              definition: typeof lookupResult.definition === 'string'
                ? lookupResult.definition
                : undefined,
              tag: lookupResult.tag ?? undefined,
              pos: lookupResult.pos ?? undefined,
              bnc: lookupResult.bnc ?? null,
              frq: lookupResult.frq ?? null,
            },
          },
        }));
        setLoading((prev) => ({ ...prev, [cleanedTerm]: false }));
        return;
      }

      try {
        const response = await fetch('/api/explain-word', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            term: cleanedTerm,
            language: userSettings?.languageSetting || 'english',
            context: sourceText,
          }),
        });

        if (!response.ok) {
          throw new Error(`API returned status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.success && data.explanation) {
          setExplanations((prev) => ({
            ...prev,
            [cleanedTerm]: data.explanation,
          }));
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (apiError) {
        console.error('Web API error:', apiError);
        throw apiError;
      }
    } catch (error) {
      console.error('Error fetching explanation:', error);
      setExplanations((prev) => ({
        ...prev,
        [cleanedTerm]: {
          translation: cleanedTerm,
          example: 'Could not load example. Please try again later.',
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [cleanedTerm]: false }));
    }
  }, [userSettings, sourceText]);


  // Use the context's isCardSaved function
  const isCardSaved = contextIsCardSaved;

  const saveCard = useCallback(async (word: string, options?: SaveCardOptions) => {
    if (isCardSaved(word)) {
      return;
    }

    if (!userSettings?.myVocabularySetting) {
      return;
    }

    if (!userId) {
      return;
    }

    // Check if we have either explanation or Youdao translation
    const hasExplanation = explanations[word];
    const hasYoudaoTranslation = options?.youdaoTranslation;

    if (!hasExplanation && !hasYoudaoTranslation) {
      console.warn(`No explanation or translation found for word: ${word}`);
      return;
    }

    setSavingWord((prev) => ({ ...prev, [word]: true }));

    try {
      // First try to save to Redis (new system)
      try {
        // Use explanation data if available, otherwise use Youdao translation data
        const translationData = hasExplanation
          ? {
              translation: explanations[word].translation || "",
              phonetic: explanations[word].usphone,
              example: explanations[word].example || "",
              source: 'original' as const,
            }
          : {
              translation: hasYoudaoTranslation?.translation || "",
              phonetic: hasYoudaoTranslation?.phonetic || hasYoudaoTranslation?.usphone,
              example: hasYoudaoTranslation?.explains?.join('; ') || "",
              source: 'youdao' as const,
              explains: hasYoudaoTranslation?.explains,
            };

        // 新增：基于 SM-2 的初始计划和分类
        const now = Date.now();
        const initialScheduling = {
          addedAt: now,
          status: (options?.status || 'NEW') as 'NEW' | 'FUZZY' | 'MASTERED',
          lastReviewedAt: null as number | null,
          nextReviewAt: now,
          stageIndex: 0,
          ease: 2.5,
          interval: 0,
          reps: 0,
          correctStreak: 0,
          wrongStreak: 0,
          bootstrapDone: false,
        };

        const redisResponse = await fetch("/api/user/saved-words", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userId,
            word: word,
            ...translationData,
            ...initialScheduling,
            // 优先使用传入的 episode 信息
            episodeId: options?.episodeId || (typeof window !== 'undefined' && (window as any).currentEpisodeId) || undefined,
            episodeTitle: options?.episodeTitle || (typeof window !== 'undefined' && (window as any).currentEpisodeTitle) || undefined,
            contextText: options?.contextText,
            episodeThumbnail: options?.episodeThumbnail,
          }),
        });

        if (redisResponse.ok) {
          const redisData = await redisResponse.json();
          if (redisData.success) {
            markWordAsSaved(word);
          }
        }
      } catch (redisError) {
        console.warn('Redis save failed, falling back to original system:', redisError);
      }

      // Show notification if at least one save method succeeded
      if (savedWords[word.toLowerCase()]) {
        if (typeof window !== 'undefined' && 'Notification' in window) {
          if (Notification.permission === 'granted') {
            new Notification('Word Saved', {
              body: `"${word}" has been added to your vocabulary.`,
              icon: '/favicon.ico'
            });
          }
        }
      }

    } catch (error) {
      console.error("Error saving vocabulary card:", error);
    } finally {
      setSavingWord((prev) => ({ ...prev, [word]: false }));
    }
  }, [isCardSaved, explanations, userSettings, userId, savedWords]);

  return {
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved
  };
} 
